import { useState, type FormEvent, type ReactElement } from 'react';
import type {
  DependentRelationship,
  DependentUpsert,
  PatientDependent,
  PatientSex,
  Uuid,
} from '@zenda/api-contract';
import { Badge } from './Badge';
import { CampoData } from './CampoData';
import { dataDeHoje, formatDateFull, toDateInput } from '../lib/format';
import { useT } from '../lib/i18n';
import { usePatientMutations } from '../lib/queries';
import { erroDoCpf, mascararCpf } from '../lib/mascaras';

const PARENTESCOS: readonly DependentRelationship[] = [
  'filho',
  'enteado',
  'neto',
  'sobrinho',
  'tutelado',
  'conjuge',
  'pai_mae',
  'outro',
];

interface Rascunho {
  name: string;
  relationship: DependentRelationship;
  birthDate: string;
  sex: PatientSex | '';
  cpf: string;
  insurer: string;
  insurancePlan: string;
  insuranceCardNumber: string;
  notes: string;
}

const VAZIO: Rascunho = {
  name: '',
  relationship: 'filho',
  birthDate: '',
  sex: '',
  cpf: '',
  insurer: '',
  insurancePlan: '',
  insuranceCardNumber: '',
  notes: '',
};

const rascunhoDe = (d: PatientDependent): Rascunho => ({
  name: d.name,
  relationship: d.relationship,
  birthDate: toDateInput(d.birthDate),
  sex: d.sex ?? '',
  cpf: d.cpf ?? '',
  insurer: d.insurer ?? '',
  insurancePlan: d.insurancePlan ?? '',
  insuranceCardNumber: d.insuranceCardNumber ?? '',
  notes: d.notes ?? '',
});

/** Vazio vai como `null`: é assim que a API apaga um campo. */
const corpoDe = (r: Rascunho): DependentUpsert => ({
  name: r.name.trim(),
  relationship: r.relationship,
  birthDate: r.birthDate === '' ? null : r.birthDate,
  sex: r.sex === '' ? null : r.sex,
  cpf: r.cpf.trim() === '' ? null : r.cpf,
  insurer: r.insurer.trim() === '' ? null : r.insurer,
  insurancePlan: r.insurancePlan.trim() === '' ? null : r.insurancePlan,
  insuranceCardNumber: r.insuranceCardNumber.trim() === '' ? null : r.insuranceCardNumber,
  notes: r.notes.trim() === '' ? null : r.notes,
});

/**
 * Os dependentes do paciente com este médico (ADR 0028).
 *
 * Arquivar e não apagar: a consulta antiga continua dizendo de quem era. O aviso de
 * consultas marcadas não impede arquivar, porque o dependente arquivado continua nelas.
 */
export function Dependentes({
  clientId,
  contactId,
  dependentes,
}: {
  readonly clientId: Uuid;
  readonly contactId: Uuid;
  readonly dependentes: readonly PatientDependent[];
}): ReactElement {
  const t = useT();
  const d = t.patients.dependentes;
  const f = t.patients.ficha;
  const { criarDependente, editarDependente } = usePatientMutations(clientId, contactId);
  /** `null`: nenhum formulário; `'novo'`: cadastro; um id: edição daquele. */
  const [editando, setEditando] = useState<Uuid | 'novo' | null>(null);
  const [r, setR] = useState<Rascunho>(VAZIO);
  const [invalido, setInvalido] = useState<string | null>(null);
  const muda = <K extends keyof Rascunho>(k: K, v: Rascunho[K]): void =>
    setR((atual) => ({ ...atual, [k]: v }));

  function abrir(alvo: PatientDependent | 'novo'): void {
    criarDependente.reset();
    editarDependente.reset();
    setInvalido(null);
    setR(alvo === 'novo' ? VAZIO : rascunhoDe(alvo));
    setEditando(alvo === 'novo' ? 'novo' : alvo.id);
  }

  function salvar(e: FormEvent): void {
    e.preventDefault();
    if (r.name.trim().length < 2) return setInvalido(d.nameRequired);
    const cpf = erroDoCpf(r.cpf);
    if (cpf !== null) return setInvalido(cpf);
    setInvalido(null);
    const fechar = { onSuccess: () => setEditando(null) };
    if (editando === 'novo') criarDependente.mutate(corpoDe(r), fechar);
    else if (editando !== null) editarDependente.mutate({ id: editando, ...corpoDe(r) }, fechar);
  }

  const erro =
    invalido !== null
      ? invalido
      : criarDependente.isError
        ? criarDependente.error.message
        : editarDependente.isError
          ? editarDependente.error.message
          : null;
  const salvando = criarDependente.isPending || editarDependente.isPending;

  const formulario = (
    <form className="form" onSubmit={salvar}>
      <div className="field">
        <label className="label" htmlFor="dep-nome">
          {d.name}
        </label>
        <input
          id="dep-nome"
          className="input input--sm"
          value={r.name}
          maxLength={120}
          onChange={(e) => muda('name', e.target.value)}
          autoComplete="off"
          autoFocus
        />
      </div>
      <div className="form__row">
        <div className="field">
          <label className="label" htmlFor="dep-parentesco">
            {d.relationship}
          </label>
          <select
            id="dep-parentesco"
            className="select select--sm"
            value={r.relationship}
            onChange={(e) => muda('relationship', e.target.value as DependentRelationship)}
          >
            {PARENTESCOS.map((p) => (
              <option key={p} value={p}>
                {d.parentescos[p]}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor="dep-nascimento">
            {d.birthDate}
          </label>
          <CampoData
            id="dep-nascimento"
            size="sm"
            value={r.birthDate}
            onChange={(v) => muda('birthDate', v)}
            max={dataDeHoje()}
          />
        </div>
      </div>
      <div className="form__row">
        <div className="field">
          <label className="label" htmlFor="dep-sexo">
            {f.campos.sex}
          </label>
          <select
            id="dep-sexo"
            className="select select--sm"
            value={r.sex}
            onChange={(e) => muda('sex', e.target.value as PatientSex | '')}
          >
            <option value="">{f.naoInformado}</option>
            <option value="F">{f.sexos.F}</option>
            <option value="M">{f.sexos.M}</option>
            <option value="I">{f.sexos.I}</option>
          </select>
        </div>
        <div className="field">
          <label className="label" htmlFor="dep-cpf">
            {f.campos.cpf}
          </label>
          <input
            id="dep-cpf"
            className="input input--sm"
            inputMode="numeric"
            value={r.cpf}
            onChange={(e) => muda('cpf', mascararCpf(e.target.value))}
            placeholder="000.000.000-00"
            autoComplete="off"
          />
        </div>
      </div>
      <div className="form__row">
        <div className="field">
          <label className="label" htmlFor="dep-operadora">
            {f.campos.insurer}
          </label>
          <input
            id="dep-operadora"
            className="input input--sm"
            value={r.insurer}
            maxLength={80}
            onChange={(e) => muda('insurer', e.target.value)}
          />
        </div>
        <div className="field">
          <label className="label" htmlFor="dep-plano">
            {f.campos.insurancePlan}
          </label>
          <input
            id="dep-plano"
            className="input input--sm"
            value={r.insurancePlan}
            maxLength={80}
            onChange={(e) => muda('insurancePlan', e.target.value)}
          />
        </div>
      </div>
      <div className="field">
        <label className="label" htmlFor="dep-carteirinha">
          {f.campos.insuranceCardNumber}
        </label>
        <input
          id="dep-carteirinha"
          className="input input--sm"
          value={r.insuranceCardNumber}
          maxLength={40}
          onChange={(e) => muda('insuranceCardNumber', e.target.value)}
        />
      </div>
      <div className="field">
        <label className="label" htmlFor="dep-notas">
          {t.patients.notesTitle}
        </label>
        <textarea
          id="dep-notas"
          className="dnote"
          value={r.notes}
          maxLength={2000}
          onChange={(e) => muda('notes', e.target.value)}
        />
      </div>
      <div className="form__row">
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => setEditando(null)}>
          {t.common.cancel}
        </button>
        <button
          type="submit"
          className="btn btn--primary btn--sm"
          disabled={salvando || r.name.trim() === ''}
        >
          {d.save}
        </button>
      </div>
    </form>
  );

  return (
    <section className="dsec">
      <div className="dsec__head">
        <span className="dsec__title">{d.title}</span>
        {editando === null ? (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => abrir('novo')}>
            {d.add}
          </button>
        ) : null}
      </div>
      <p className="dsec__hint">{d.hint}</p>
      {erro === null ? null : <p className="form__error">{erro}</p>}

      {dependentes.length === 0 && editando === null ? (
        <p className="dlist__empty">{d.none}</p>
      ) : null}

      {dependentes.length === 0 ? null : (
        <div className="dlist">
          {dependentes.map((x) =>
            editando === x.id ? (
              <div key={x.id}>{formulario}</div>
            ) : (
              <div className="dlist__item" key={x.id}>
                <div className="dlist__main">
                  <span className="dlist__name">{x.name}</span>
                  <span className="dlist__meta">
                    {d.parentescos[x.relationship]}
                    {x.birthDate === null ? '' : ` · ${formatDateFull(x.birthDate)}`}
                    {x.upcomingAppointments === 0
                      ? ''
                      : ` · ${d.archiveWarning(x.upcomingAppointments)}`}
                  </span>
                </div>
                {x.archivedAt === null ? null : <Badge tone="slate">{d.archived}</Badge>}
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={editando !== null}
                  onClick={() => abrir(x)}
                >
                  {d.edit}
                </button>
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  disabled={editarDependente.isPending || editando !== null}
                  title={
                    x.archivedAt === null && x.upcomingAppointments > 0
                      ? d.archiveWarning(x.upcomingAppointments)
                      : undefined
                  }
                  onClick={() =>
                    editarDependente.mutate({ id: x.id, archived: x.archivedAt === null })
                  }
                >
                  {x.archivedAt === null ? d.archive : d.unarchive}
                </button>
              </div>
            ),
          )}
        </div>
      )}

      {editando === 'novo' ? formulario : null}
    </section>
  );
}
